import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import { digObject } from '@domain-group/javascript-utils'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import usePublicationOption, { usePublicationOptionForm } from '@hooks/usePublicationOption'

import PublicationOptionValueForm from './PublicationOptionValueForm'

const confirmDelete = (deletePublicationOption, deleteFn, closeModal) => {
  const publicationOptionPayload = {
    callbacks: {
      action: deleteFn,
      afterAction: closeModal,
    },
    toastText: 'Publication Option Value Deleted',
  }

  swal
    .fire({
      title: 'Delete Publication Option?',
      text: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it.',
      confirmButtonColor: '#DD6B55',
      animation: 'slide-from-top',
    })
    .then(({ value }) => {
      if (value){
        deletePublicationOption(publicationOptionPayload)
      }
    })
}

const createOrUpdatePublicationOption = (entityState, props, createFn, updateFn) => {
  const {
    callbacks: { closeModal, createPublicationOption, updatePublicationOption },
  } = props

  const actionType = entityState.id ? updateFn : createFn
  const actionText = entityState.id ? 'Updated' : 'Created'

  const publicationOptionPayload = {
    callbacks: {
      action: actionType,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Publication Option ${actionText}`,
  }

  if (entityState.id) return updatePublicationOption(publicationOptionPayload)

  return createPublicationOption(publicationOptionPayload)
}

const CreateOrEditPublicationOptionsModal = (props) => {
  const { callbacks, modalKey, showModal } = props
  const {
    callbacks: {
      closeModal,
      deletePublicationOption,
    },
  } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const {
    publicationOption: initPublicationOption,
  } = modalPayload

  const optionPayload = usePublicationOption(initPublicationOption)
  const {
    callbacks: {
      createPublicationOption: createFn,
      deletePublicationOption: deleteFn,
      updatePublicationOption: updateFn,
    },
    creating,
    deleting,
    publicationOption,
    updating,
  } = optionPayload

  const optionFormPayload = usePublicationOptionForm(publicationOption, { validateOn: 'change' })
  const {
    entityState,
    entityState: {
      id,
      input_type,
      node_name,
      placeholder_text,
      title,
    },
    errors,
    handlers,
    saveEnabled,
  } = optionFormPayload

  const saveOrCreateButtonText = id ? 'Save Publication Option' : 'Create Publication Option'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Add'} titleSecondLine="Publication Option" />

      <SidebarModal.Content>
        <Form>
          <Form.Field errorMessage={errors.title} label="* Title">
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field errorMessage={errors.node_name} label="* Node Name" marginTop="large">
            <input name="node_name" type="text" value={node_name} {...handlers} />
          </Form.Field>

          <Form.Field errorMessage={errors.input_type} label="* Input Type" marginTop="large">
            <select name="input_type" value={input_type} {...handlers}>
              <option value="">Please Select...</option>
              <option value="input">Input Field</option>
              <option value="select">Select Field</option>
            </select>
          </Form.Field>

          {input_type === 'input' && (
            <Form.Field label="Placeholder Text" marginTop="large">
              <input name="placeholder_text" type="text" value={placeholder_text} {...handlers} />
            </Form.Field>
          )}

          {id && input_type === 'select' && (
            <PublicationOptionValueForm
              publication_option_id={publicationOption.id}
            />
          )}

          {id && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                onClick={() => confirmDelete(deletePublicationOption, deleteFn, closeModal)}
                size="medium"
              >
                Delete Publication Option
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdatePublicationOption(entityState, props, createFn, updateFn)}
          size="large"
        >
          {saveEnabled ? saveOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditPublicationOptionsModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

CreateOrEditPublicationOptionsModal.defaultProps = {
  modalKey: 'CreateOrEditPublicationOptionsModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditPublicationOptionsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
